import React, { memo, useState, useEffect } from 'react';
import axios from 'axios';
import Lottie from 'lottie-react';
import { motion, AnimatePresence } from 'framer-motion';

// Replace with actual asset imports
import octopusAnimation from '../assets/Octopus.json';
import footerImage from '../assets/Footer.png';
import gameImage from '../assets/Game.jpg';
import schoolWarImage from '../assets/SchoolWar.png';

// Set Axios base URL
axios.defaults.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// TypeScript interfaces
interface School {
  _id: string;
  schoolName: string;
  city: string;
  points: number;
  schoolImage?: string;
  rank: number;
}

// Mock data (Removed schoolImage for top 3 schools)
const mockSchools: School[] = [
  { _id: '1', schoolName: 'Alpha International School', city: 'Chennai', points: 9500, rank: 1 },
  { _id: '2', schoolName: 'Velammal Vidhyashram', city: 'Chennai', points: 9200, rank: 2 },
  { _id: '3', schoolName: 'National Academy for Learning', city: 'Chennai', points: 8900, rank: 3 },
  { _id: '4', schoolName: 'Marias Public School', city: 'Guwahati', points: 8500, rank: 4 },
  { _id: '5', schoolName: 'Greenwood High School', city: 'Banglore', points: 8200, rank: 5 },
];

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } },
};

// Leaderboard Card Component (Updated for Vertical Stacking, Trophy Removed)
const LeaderboardCard = memo(({ school, index }: { school: School; index: number }) => (
  <motion.div
    className={`p-4 rounded-xl bg-white/90 backdrop-blur-md shadow-md flex items-start gap-3 transition-all duration-300 ${
      index < 3 ? 'border-2 border-amber-400' : 'border border-gray-200'
    }`}
    variants={fadeInUp}
    whileHover={{ y: -5, boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)' }}
    whileTap={{ scale: 0.98 }}
    role="listitem"
    aria-label={`Rank ${school.rank}: ${school.schoolName}`}
  >
    <div className="text-xl font-semibold text-gray-800 w-10 text-center flex-shrink-0">
      {school.rank}
    </div>
    {school.schoolImage && (
      <img
        src={school.schoolImage}
        alt={`${school.schoolName} logo`}
        className="w-12 h-12 rounded-full object-cover border-2 border-blue-500 flex-shrink-0"
        loading="lazy"
      />
    )}
    <div className="flex-1 flex flex-col gap-1">
      <h3 className="text-base font-semibold text-blue-600 truncate">{school.schoolName}</h3>
      <p className="text-sm text-gray-600">{school.city}</p>
      <div className="text-base font-bold text-gray-800">{school.points} Pts</div>
    </div>
  </motion.div>
));

LeaderboardCard.displayName = 'LeaderboardCard';

// Game Section Component
const GameSection = memo(() => (
  <motion.section
    className="bg-white/90 backdrop-blur-md p-5 rounded-xl shadow-md text-center"
    initial="hidden"
    animate="visible"
    variants={fadeInUp}
  >
    <motion.img
      src={gameImage}
      alt="School Wars Game"
      loading="lazy"
      className="w-full max-w-md mx-auto rounded-lg shadow-sm"
      whileHover={{ scale: 1.03 }}
      transition={{ duration: 0.3 }}
    />
  </motion.section>
));

GameSection.displayName = 'GameSection';

// Main SchoolRankings Component
const SchoolRankings: React.FC = () => {
  const [schools, setSchools] = useState<School[]>(mockSchools);
  const [loading, setLoading] = useState(false);

  // Uncomment to fetch from API
  /*
  useEffect(() => {
    const fetchSchools = async () => {
      setLoading(true);
      try {
        const response = await axios.get('/api/schools/rankings');
        setSchools(response.data.schools.map((school: School, index: number) => ({
          ...school,
          rank: index + 1,
        })));
      } catch (err) {
        console.error('Failed to fetch rankings:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSchools();
  }, []);
  */

  return (
    <div className="relative min-h-screen bg-gray-50">
      <style>{`
        :root {
          --highlight-black: #1a202c;
          --base-white: #ffffff;
          --glass-bg: rgba(255, 255, 250, 0.2);
          --card-blur: 10px;
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        html, body {
          background-color: var(--base-white);
          color: var(--highlight-black);
          margin: 0;
          overflow-x: hidden;
          scroll-behavior: smooth;
          -webkit-overflow-scrolling: touch;
        }

        .ranking-container {
          font-family: 'Inter', sans-serif;
          background: linear-gradient(to bottom, #f7fafc, #e2e8f0);
          min-height: 100vh;
          padding: 0 4vw;
        }

        .ranking-header {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 50;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(6px);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          height: 80px;
          max-height: 80px;
          padding: 8px 4vw;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .ranking-header .max-w-7xl {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          max-width: 1200px;
          height: 100%;
        }

        .ranking-header .flex.items-center.gap-2 {
          display: flex;
          align-items: center;
          gap: 8px;
          max-height: 64px;
        }

        .ranking-header .w-8.h-8.sm\\:w-10.sm\\:h-10 {
          width: 32px;
          height: 32px;
          max-height: 32px;
        }

        .ranking-header .text-lg.sm\\:text-xl {
          font-size: 16px;
          line-height: 20px;
          font-weight: 700;
          white-space: nowrap;
        }

        .cta-button {
          background: linear-gradient(135deg, #3b82f6, #7c3aed);
          color: var(--base-white);
          padding: 8px 16px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          max-height: 36px;
        }

        .cta-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }

        .cta-button:active {
          transform: translateY(0);
        }

        .ranking-main {
          padding-top: 96px;
          padding-bottom: 8vh;
          max-width: 1200px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          gap: 6vw;
        }

        .leaderboard-section {
          background: var(--glass-bg);
          backdrop-filter: blur(var(--card-blur));
          border-radius: 12px;
          padding: 4vw;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
        }

        .school-war-image {
          margin-top: 2rem;
          margin-bottom: 3rem;
          max-width: 60%;
          height: auto;
          display: block;
          margin-left: auto;
          margin-right: auto;
          border-radius: 12px;
        }

        /* Responsive Design */
        @media (max-width: 640px) {
          .ranking-main {
            padding-top: 88px;
            padding-bottom: 10vh;
            gap: 8vw;
          }

          .ranking-header {
            padding: 8px 3vw;
            height: 80px;
            max-height: 80px;
          }

          .ranking-header .w-8.h-8.sm\\:w-10.sm\\:h-10 {
            width: 28px;
            height: 28px;
            max-height: 28px;
          }

          .ranking-header .text-lg.sm\\:text-xl {
            font-size: 14px;
            line-height: 18px;
          }

          .cta-button {
            padding: 6px 12px;
            font-size: 12px;
            max-height: 32px;
          }

          .leaderboard-section {
            padding: 3vw;
          }

          h1 {
            font-size: clamp(24px, 8vw, 28px) !important;
          }

          p {
            font-size: clamp(14px, 4vw, 16px) !important;
          }

          .school-war-image {
            margin-top: 1.5rem;
            margin-bottom: 2rem;
            border-radius: 8px;
          }
        }

        /* High-density displays */
        @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
          img {
            image-rendering: -webkit-optimize-contrast;
          }
        }

        /* Accessibility */
        .sr-only {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          border: 0;
        }
      `}</style>

      <motion.div
        className="ranking-container"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <motion.header
          className="ranking-header"
          initial={{ y: -100 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Lottie
                animationData={octopusAnimation}
                loop={3}
                className="w-8 h-8 sm:w-10 sm:h-10"
                aria-label="drOcto Logo Animation"
              />
              <span className="text-lg sm:text-xl font-bold text-gray-800">School Wars</span>
            </div>
            <motion.a
              href="https://wa.me/+918472958581"
              target="_blank"
              rel="noopener noreferrer"
              className="cta-button"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Join Next Season"
            >
              Join
            </motion.a>
          </div>
        </motion.header>

        <main className="ranking-main">
          <motion.section className="text-center" variants={fadeInUp} initial="hidden" animate="visible">
            <motion.img
              src={schoolWarImage}
              alt="School Wars Banner"
              className="school-war-image"
              variants={fadeInUp}
              loading="lazy"
            />
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-800 mb-3 leading-tight">
              School Wars 2025 Leaderboard
            </h1>
            <p className="text-base sm:text-lg text-gray-600 mb-4 max-w-2xl mx-auto">
              Discover top schools competing in School Wars, showcasing excellence in AI-powered learning.
            </p>
          </motion.section>

          <motion.section className="leaderboard-section" variants={fadeInUp} initial="hidden" animate="visible">
            <motion.div className="space-y-3" variants={staggerContainer} role="list" aria-label="Leaderboard">
              {loading ? (
                <p className="text-gray-600 text-center">Loading rankings...</p>
              ) : schools.length > 0 ? (
                schools.map((school, index) => (
                  <LeaderboardCard key={school._id} school={school} index={index} />
                ))
              ) : (
                <p className="text-gray-600 text-center">No schools available.</p>
              )}
            </motion.div>
          </motion.section>

          <GameSection />

          <motion.section
            className="text-center bg-gradient-to-r from-blue-600 to-indigo-700 p-5 rounded-xl shadow-lg text-white"
            variants={fadeInUp}
            initial="hidden"
            animate="visible"
          >
            <h2 className="text-xl sm:text-2xl font-bold mb-3">Ready to Join School Wars?</h2>
            <p className="text-sm sm:text-base mb-4 max-w-xl mx-auto">
              Be part of the next season and compete with the best schools worldwide.
            </p>
            <motion.a
              href="https://wa.me/+918472958581"
              target="_blank"
              rel="noopener noreferrer"
              className="cta-button bg-white text-blue-600"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Join Now"
            >
              Join Now
            </motion.a>
          </motion.section>
        </main>

        <footer className="bg-gray-900 text-white py-8 px-4">
          <div className="max-w-7xl mx-auto text-center">
            <img
              src={footerImage}
              alt="drOcto Footer Logo"
              loading="lazy"
              className="mx-auto mb-4 max-w-[150px] sm:max-w-[200px]"
            />
            <p className="text-sm sm:text-base mb-3">Empowering the Future of Education</p>
            <p className="text-xs sm:text-sm">© {new Date().getFullYear()} drOcto. All rights reserved.</p>
          </div>
        </footer>
      </motion.div>

      {/* PWA and Mobile Meta Tags */}
      <head>
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
        <meta name="theme-color" content="#3b82f6" />
        <link rel="manifest" href="/manifest.json" />
      </head>
    </div>
  );
};

export default SchoolRankings;